import { FC, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  School as SchoolIcon,
  HelpOutline as HelpIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useAuthUserState } from "../../../context/authUser.context";
import { useInstituteContext } from "../../../context/institute.context";
import { useHelpContext } from "../../../context/help.context";
import { useNavigate, useLocation } from "react-router-dom";
import { pages } from "../../../utils/common";
import { useDashboardHook } from "../../../hooks/useDashboardHook";
import { canAccessInstituteLevelPages } from "../../../utils/userTypes";
import { formatInstituteStatusLabel } from "../../modules/institute/institute.helpers";

export type HeaderToolbarProps = {
  pageTitle: string;
  drawerWidth: number;
  drawerOpen: boolean;
};

const HeaderToolbar: FC<HeaderToolbarProps> = ({
  pageTitle,
  drawerWidth,
  drawerOpen,
}) => {
  const { userDetails } = useAuthUserState();
  const { selectedInstitute, setSelectedInstitute } = useInstituteContext();
  const { data: dashboardData } = useDashboardHook();
  const { showHelp, isHelpEnabled } = useHelpContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [instituteMenuAnchor, setInstituteMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [instituteSearch, setInstituteSearch] = useState("");

  const canAccessInstitutePages = canAccessInstituteLevelPages(userDetails);

  const handleHelpClick = () => {
    const pathname = location.pathname;
    // Find matching page in pages object
    const pageKey = Object.keys(pages).find((key) => {
      const page = pages[key];
      return (
        page.path === pathname ||
        page.aliasPath === pathname ||
        pathname.startsWith(page.path + "/")
      );
    });
    const screenId = pageKey || "dashboard";
    showHelp(screenId);
  };

  const handleHelpPageClick = () => {
    navigate(pages.help.path);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate(pages.profile.path);
  };

  const handleInstituteMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setInstituteMenuAnchor(event.currentTarget);
  };

  const handleInstituteMenuClose = () => {
    setInstituteMenuAnchor(null);
    setInstituteSearch("");
  };

  const filteredInstitutes = useMemo(() => {
    const list = dashboardData?.institutes ?? [];
    const q = instituteSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((inst) => inst.name.toLowerCase().includes(q));
  }, [dashboardData?.institutes, instituteSearch]);

  const handleInstituteSelect = (instituteId: string) => {
    const institute = dashboardData?.institutes?.find(
      (inst) => inst.id === instituteId,
    );
    if (institute) {
      setSelectedInstitute(institute);
    }
    handleInstituteMenuClose();
    if (pageTitle !== "Dashboard") {
      navigate(pages.dashboard.path);
    }
  };

  const getUserDisplayName = () => {
    if (userDetails?.firstName && userDetails?.lastName) {
      return `${userDetails.firstName} ${userDetails.lastName}`;
    }
    return userDetails?.username || userDetails?.email || "User";
  };

  const getUserInitials = () => {
    if (userDetails?.firstName && userDetails?.lastName) {
      return `${userDetails.firstName[0]}${userDetails.lastName[0]}`.toUpperCase();
    }
    if (userDetails?.username) {
      return userDetails.username[0].toUpperCase();
    }
    return "U";
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: drawerOpen
          ? `calc(100% - ${drawerWidth}px)`
          : `calc(100% - 64px)`,
        ml: drawerOpen ? `${drawerWidth}px` : "64px",
        transition: (theme) =>
          theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
      }}
      data-testid="header-toolbar"
    >
      <Toolbar data-testid="header-toolbar-content">
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{ flexGrow: 1 }}
          data-testid="header-page-title"
        >
          {pageTitle}
        </Typography>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2 }}
          data-testid="header-institute-selector-container"
        >
          {/* Institute Selector - Only shown for standard users */}
          {canAccessInstitutePages &&
            dashboardData?.institutes &&
            dashboardData.institutes.length > 0 && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={handleInstituteMenuOpen}
                  data-testid="institute-selector-button"
                >
                  <SchoolIcon fontSize="small" />
                  {selectedInstitute ? (
                    <Chip
                      label={selectedInstitute.name}
                      size="small"
                      color="info"
                      variant="special"
                      data-testid="selected-institute-chip"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 500,
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select Institute
                    </Typography>
                  )}
                </Box>
                <Menu
                  anchorEl={instituteMenuAnchor}
                  open={Boolean(instituteMenuAnchor)}
                  onClose={handleInstituteMenuClose}
                  disableAutoFocus
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  sx={{
                    '& ul': {
                      height: '100%',
                      overflowY: 'hidden',
                    },
                    '& ul ul': {
                      height: 'calc(100% - 60px)',
                      overflowY: 'auto',
                    }
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        height: 400,
                        maxHeight: 400,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        minWidth: 280,
                      },
                    },
                  }}
                  data-testid="institute-menu"
                >
                  <Box
                    sx={{
                      p: 1,
                      flexShrink: 0,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <TextField
                      autoFocus
                      size="small"
                      fullWidth
                      placeholder="Search institutes…"
                      value={instituteSearch}
                      onChange={(e) => setInstituteSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      inputProps={{ "data-testid": "institute-menu-search" }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <MenuList
                    dense
                    autoFocusItem={false}
                    sx={{ flex: 1, overflowY: "auto", py: 0, height: '100%' }}
                  >
                    {filteredInstitutes.length === 0 ? (
                      <MenuItem disabled data-testid="institute-menu-no-matches">
                        No matching institutes
                      </MenuItem>
                    ) : (
                      filteredInstitutes.map((institute) => (
                        <MenuItem
                          key={institute.id}
                          onClick={() => handleInstituteSelect(institute.id)}
                          selected={selectedInstitute?.id === institute.id}
                          data-testid={`institute-menu-item-${institute.id}`}
                        >
                          <Box>
                            <Typography variant="body2">
                              {institute.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatInstituteStatusLabel(institute.status)} •{" "}
                              {institute.configurationCompleteness}% complete
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </MenuList>
                </Menu>
              </>
            )}
          {/* Display assigned institute name as read-only for restricted users */}
          {!canAccessInstitutePages && selectedInstitute && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.5,
              }}
              data-testid="assigned-institute-display"
            >
              <SchoolIcon fontSize="small" />
              <Chip
                label={selectedInstitute.name}
                size="small"
                color="info"
                variant="outlined"
                data-testid="assigned-institute-chip"
                sx={{
                  textTransform: "capitalize",
                  fontWeight: 500,
                  backgroundColor: "background.paper",
                }}
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          data-testid="header-actions-container"
        >
          {isHelpEnabled && (
            <Tooltip title="Get help for this page">
              <IconButton
                size="small"
                onClick={handleHelpClick}
                color="inherit"
                sx={{ mr: 1 }}
                data-testid="help-button"
              >
                <HelpIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default HeaderToolbar;
